<mat-card class="card-table has-progress-bar" *ngIf="patientDataService.patient$ | async as patient">
  <mat-progress-bar [mode]="(loading$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>
  <mat-card-title fxLayout="row" fxLayoutAlign="space-between center">
    <mmx-authorization-status-icon
      *ngIf="appointment"
      class="mr-3"
      [patient]="patient"
      [appointment]="appointment"
      (authorizationSaved)="data.appointmentUpdated$.next()"
    ></mmx-authorization-status-icon>
    <span>{{ 'patient-details.authorization-card.title' | translate }}</span>
    <div fxFlex></div>
    <div>
      <ng-container *ngIf="whyCantPullAuths$ | async as whyCantPullAuths">
        <span class="pr-3">{{ whyCantPullAuths }}</span>
      </ng-container>
      <mmx-appointment-portal-menu
        *ngIf="appointment?.portalRules"
        type="authorization"
        [appointment]="appointment"
      >
      </mmx-appointment-portal-menu>
      <button
        *appHasPermission="'prior-authorization.pull'"
        mat-stroked-button
        class="mr-2"
        (click)="openPullServiceReviewsDialog(patient)"
        [disabled]="(whyCantPullAuths$ | async) !== null"
      >
        <mat-icon>download</mat-icon>
        <span>{{ 'patient-details.authorization-card.button-pull' | translate }}</span>
      </button>
      <button
        *appHasPermission="Permission.AUTHORIZATION_CREATE"
        mat-stroked-button
        (click)="openAuthorizationDialog()"
      >
        <mat-icon>add</mat-icon>
        <span>{{ 'patient-details.authorization-card.button-add' | translate }}</span>
      </button>
    </div>
  </mat-card-title>
  <mat-card-content fxLayout="column">
    <div *ngIf="appointment?.authorizationStatuses?.includes(AUTHORIZATION_STATUS.NOT_KNOWN)" class="mmx-card-padding">
      {{ 'cards.authorizations.may-be-required' | translate }}
    </div>

    <div *ngIf="appointment?.authorizationStatuses?.includes(AUTHORIZATION_STATUS.NOT_REQUIRED)" class="mmx-card-padding">
      {{ 'cards.authorizations.not-required' | translate }}
    </div>

    <div *ngIf="appointment?.authorizationStatuses?.includes(AUTHORIZATION_STATUS.NOT_INITIATED)" class="mmx-card-padding text-warn">
      {{ 'cards.authorizations.required' | translate }}
    </div>

    <ng-container *ngIf="(loading$ | async) === false; else loadingEl">
      <ng-container *ngIf="authorizations$ | async as authorizations">
        <ng-container *ngIf="authorizations.length > 0; else noAuthorizations">
          <table mat-table [dataSource]="authorizations">
            <ng-container matColumnDef="effectiveDate">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.effective-date' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                <div *ngIf="authorization.startAt || authorization.endAt" class="nowrap">
                  {{ authorization.startAt | momentDate }}
                  &mdash;
                  {{ authorization.endAt | momentDate }}
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="insurance">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'common.insurance' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                <ng-container *ngIf="getInsurance(authorization) | async as insurance">
                  {{ insurance.clinicCarrierName || insurance.clinicCarrierId }}
                </ng-container>
              </td>
            </ng-container>

            <ng-container matColumnDef="number">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.table.numbers' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                <div fxLayout="column" fxLayoutGap="5px">
                  <div *ngIf="authorization.number" [matTooltip]="'patient-details.authorization-card.table.certification-number' | translate">
                    <!-- {{ 'patient-details.authorization-card.table.certification-number' | translate }}: -->
                    <mmx-copy-text>{{ authorization.number }}</mmx-copy-text>
                  </div>
                  <div *ngIf="authorization.referenceNumber" [matTooltip]="'patient-details.authorization-card.table.reference-number' | translate">
                    <!-- {{ 'patient-details.authorization-card.table.reference-number' | translate }}: -->
                    <mmx-copy-text>{{ authorization.referenceNumber }}</mmx-copy-text>
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="referenceNumber">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.table.reference-number' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
              </td>
            </ng-container>

            <ng-container matColumnDef="authorizationStatusText">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'common.status' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
                  <span *ngIf="authorization.status" class="nowrap">
                    {{ ('statuses.authorizations.' + authorization.status) | translate }}
                  </span>

                  <mat-icon *ngIf="isOutdated(authorization)" color="warn" [matTooltip]="'patient-details.authorization-card.outdated' | translate">warning</mat-icon>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="attachedTo">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.table.attached-to' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                <a *ngIf="authorization.appointmentIds?.size; else notAssigned"
                  [matMenuTriggerFor]="attachedAppointmentsMenu"
                  (menuOpened)="fetchAppointments$.next()"
                >{{ authorization.appointmentIds?.size }}</a>
                <ng-template #notAssigned>0</ng-template>
                <mat-menu #attachedAppointmentsMenu="matMenu">
                  <ng-container *ngIf="getAttachedAppointments(authorization) | async as appointments">
                    <button *ngFor="let apt of appointments" mat-menu-item [disabled]="apt.id === appointment.id" [routerLink]="apt.url">
                      {{ apt.text }}
                    </button>
                  </ng-container>
                </mat-menu>
              </td>
            </ng-container>

            <ng-container matColumnDef="quantity">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.limitations' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                {{ authorization.limitations?.quantity }}
                <ng-container *ngIf="authorization.limitations?.quantityType">
                  {{ ('patient-details.authorization-card.quantity-types.' + authorization.limitations?.quantityType) | translate }}
                </ng-container>
              </td>
            </ng-container>

            <ng-container matColumnDef="note">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'common.notes' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                {{ authorization.note }}
              </td>
            </ng-container>

            <ng-container matColumnDef="updatedBy">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'patient-details.authorization-card.table.updated-by' | translate }}
              </th>
              <td mat-cell *matCellDef="let authorization">
                {{ authorization.updatedBy?.name }}
              </td>
            </ng-container>

            <ng-container matColumnDef="edit">
              <th mat-header-cell *matHeaderCellDef>
              </th>
              <td mat-cell *matCellDef="let authorization">
                <button
                  mat-icon-button
                  [matMenuTriggerFor]="menu"
                >
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu">
                  <button
                    *appHasPermission="Permission.AUTHORIZATION_EDIT"
                    mat-menu-item
                    (click)="openAuthorizationDialog(authorization)"
                  >
                    <mat-icon>edit</mat-icon>
                    <span>{{ 'patient-details.authorization-card.button-edit' | translate }}</span>
                  </button>
                  <!-- <ng-container *appHasPermission="Permission.AUTHORIZATION_EDIT">
                    <button
                      *ngIf="isAuthSubmissionSupported(authorization)"
                      mat-menu-item
                      (click)="openSubmitToPayerDialog(patient, authorization)"
                    >
                      <mat-icon>upload</mat-icon>
                      <span>{{ 'patient-details.authorization-card.table.submit-to-payer' | translate }}</span>
                    </button>
                  </ng-container> -->
                  <button
                    *ngIf="authorization.fullAuthorizationObject"
                    mat-menu-item (click)="openRawResponse(authorization)">
                    <mat-icon>code</mat-icon>
                    <span>{{ 'patient-details.eligibility-card.view-raw-response' | translate }}</span>
                  </button>
                  <button
                    mat-menu-item
                    (click)="showHistory(authorization)">
                    <mat-icon>history</mat-icon>
                    <span>{{ 'common.history' | translate }}</span>
                  </button>
                  <button
                    mat-menu-item
                    [matMenuTriggerFor]="attachmentsMenu"
                    [disabled]="!authorization.attachments?.length"
                  >
                    <mat-icon>attach_file</mat-icon>
                    <span>{{ 'patient-details.authorization-card.table.attachments' | translate }}</span>
                  </button>
                  <ng-container *appHasPermission="'authorization.delete'">
                    <mat-divider></mat-divider>
                    <button mat-menu-item mmxConfirmClick message="Are you sure you want to delete this authorization?"
                      (confirm)="deleteAuthorization(authorization)">
                      <mat-icon color="warn">delete</mat-icon>
                      {{ 'common.delete' | translate }}
                    </button>
                  </ng-container>
                </mat-menu>

                <mat-menu #attachmentsMenu="matMenu">
                  <button
                    *ngFor="let attachment of authorization.attachments"
                    mat-menu-item
                    (click)="viewFile(authorization, attachment)"
                  >
                    {{ attachment.fileName }}
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <ng-container matColumnDef="appointment">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let authorization">
                <mat-checkbox
                  matTooltip="Attach authorization to this appointment"
                  [checked]="checkboxChecked(authorization)"
                  (change)="checkboxChanged(authorization)">
                </mat-checkbox>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
          </table>
        </ng-container>
      </ng-container>
    </ng-container>
  </mat-card-content>

  <ng-template #noAuthorizations>
    <div class="mmx-card-padding">
      {{ 'patient-details.authorization-card.no-authorization' | translate }}
    </div>
  </ng-template>

  <ng-template #loadingEl>
    <div class="text-center mmx-card-padding mt-5 mb-5">
      {{ 'patient-details.authorization-card.loading' | translate }}
    </div>
  </ng-template>
</mat-card>
